/*
  Product cards in a row line up without fixed reservations.

  Every card in a row stretches to the tallest card and its buy button sits at the bottom
  (featured-card-product.css). Before this script the title, price and rating slots also reserved
  a fixed height (two title lines, a two-line price, a 24px rating line) so prices and ratings stayed
  level across a row, which left the same blank bands in every card of a row that did not need them.
  Here each slot takes the height of the tallest one in its row instead, and a rating line that is
  empty in every card of the row is hidden. Rows are the cards of one list (grid, carousel, search
  panel) that share a top edge. It runs again when cards are added or change (swatch, price,
  Okendo stars) and when the layout width changes.
*/
(function () {
  'use strict';

  var CARD = '.featured-card-product';
  var SLOTS = ['.feature-card_product-title', '.product_card_price', '.oke-sr-cont'];
  var RATING_INDEX = 2;
  var scheduled = false;

  function isVisible(el) {
    return el.offsetParent !== null || getComputedStyle(el).position === 'fixed';
  }

  function hasRating(el) {
    if (!el) return false;
    if (el.querySelector('svg, img, .oke-sr-count-number, .oke-stars')) return true;
    return el.textContent.trim() !== '';
  }

  function listOf(card) {
    return card.closest('ul, ol, .grid, .product-grid, .predictive-search, .slider') || document.body;
  }

  // Layout is read and written in separate passes (all reads, then all writes). The earlier loop
  // reset a card's slots, then read the next card's visibility, and wrote each row's heights between
  // the row measurements, so every step forced a fresh layout of the page: with a few dozen cards a
  // swatch tap spent most of its time here. The slots' heights depend only on their own content and
  // the card width, never on a sibling's min-height, so the result is the same.
  function align() {
    scheduled = false;

    // Pass 1, reads only: the cards that are shown.
    var shown = [];
    document.querySelectorAll(CARD).forEach(function (card) {
      if (isVisible(card)) shown.push(card);
    });

    // Pass 2, writes only: clear what the last run set.
    var groups = new Map();
    shown.forEach(function (card) {
      var slots = SLOTS.map(function (selector) {
        return card.querySelector(selector);
      });
      slots.forEach(function (slot, index) {
        if (!slot) return;
        slot.style.minHeight = '';
        if (index === RATING_INDEX) slot.classList.remove('card-rows__empty');
      });
      var list = listOf(card);
      if (!groups.has(list)) groups.set(list, new Map());
      groups.get(list).set(card, slots);
    });

    // Pass 3, reads only: row membership and the tallest slot of every row.
    var hide = [];
    var heights = [];
    groups.forEach(function (cards) {
      var rows = new Map();
      cards.forEach(function (slots, card) {
        var top = Math.round(card.getBoundingClientRect().top + window.scrollY);
        var key = null;
        rows.forEach(function (_, rowTop) {
          if (key === null && Math.abs(rowTop - top) <= 2) key = rowTop;
        });
        if (key === null) {
          key = top;
          rows.set(key, []);
        }
        rows.get(key).push(slots);
      });

      rows.forEach(function (row) {
        SLOTS.forEach(function (_, index) {
          var present = row
            .map(function (slots) {
              return slots[index];
            })
            .filter(Boolean);
          if (!present.length) return;
          if (index === RATING_INDEX && !present.some(hasRating)) {
            hide.push(present);
            return;
          }
          var tallest = 0;
          present.forEach(function (slot) {
            tallest = Math.max(tallest, slot.getBoundingClientRect().height);
          });
          if (present.length > 1) heights.push([present, Math.ceil(tallest) + 'px']);
        });
      });
    });

    // Pass 4, writes only.
    hide.forEach(function (present) {
      present.forEach(function (slot) {
        slot.classList.add('card-rows__empty');
      });
    });
    heights.forEach(function (entry) {
      entry[0].forEach(function (slot) {
        slot.style.minHeight = entry[1];
      });
    });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(align);
  }

  // A card whose width changes, or that goes from hidden to shown (a featured collection tab, a
  // drawer), needs its row measured again. Height changes are ignored: aligning changes them.
  var widths = new WeakMap();
  var resizeObserver = window.ResizeObserver
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          var width = Math.round(entry.contentRect.width);
          if (widths.get(entry.target) !== width) {
            widths.set(entry.target, width);
            schedule();
          }
        });
      })
    : null;

  function observeCards(root) {
    if (!resizeObserver) return;
    if (root.matches && root.matches(CARD)) resizeObserver.observe(root);
    if (root.querySelectorAll) root.querySelectorAll(CARD).forEach(function (card) {
      resizeObserver.observe(card);
    });
  }

  // The shade sheet sits over the card (absolute in it, fixed on phones) and never changes a slot's
  // height, so changes inside it, and moving it between the card and <body>, are not layout changes
  // for the row. Opening and closing it used to re-measure every card on the page twice. Comments (the
  // sheet's placeholder) never take space either.
  var MODAL = '[data-shade-select-modal]';
  // The sheet first sits in the card as inert text (script[data-shade-sheet-html], see
  // product-card-swatches.js materializeShadeSheet); swapping it for the sheet is not a layout change either.
  var SHEET_OR_HOLDER = MODAL + ', script[data-shade-sheet-html]';

  function isSheetOrComment(node) {
    return node.nodeType === 8 || (node.nodeType === 1 && node.matches(SHEET_OR_HOLDER));
  }

  function onlySheetMoved(mutation) {
    if (mutation.type !== 'childList') return false;
    if (!mutation.addedNodes.length && !mutation.removedNodes.length) return false;
    return Array.prototype.every.call(mutation.addedNodes, isSheetOrComment) &&
      Array.prototype.every.call(mutation.removedNodes, isSheetOrComment);
  }

  // New cards, and new content inside a card (a swatch or price update, Okendo's stars).
  function touchesCards(mutation) {
    var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
    if (target && target.closest(MODAL)) return false;
    if (onlySheetMoved(mutation)) return false;
    var touched = !!(target && target.closest(CARD));
    for (var i = 0; i < mutation.addedNodes.length; i++) {
      var node = mutation.addedNodes[i];
      if (node.nodeType === 1 && (node.matches(CARD) || node.querySelector(CARD))) {
        observeCards(node);
        touched = true;
      }
    }
    return touched;
  }

  function start() {
    document.documentElement.classList.add('card-rows');
    observeCards(document);
    align();
    new MutationObserver(function (mutations) {
      if (mutations.filter(touchesCards).length) schedule();
    }).observe(document.body, {childList: true, subtree: true, characterData: true});
    window.addEventListener('load', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
